import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChalkboardTeacher,
  Users,
  ClipboardText,
  Camera,
  Clock,
  CalendarCheck,
  ShieldCheck,
  TrendUp,
  ForkKnife,
  CaretRight,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useOrganizationConfig } from "@/context/OrganizationConfigContext";
import PWAInstallCard from "@/components/PWAInstallCard";
import {
  listChildrenInClassroom,
  listReportsForChild,
  listTeacherClassrooms,
} from "@/lib/dailyReports";
import { todayStr } from "@/lib/entryTypes";

export default function TeacherDashboard() {
  const { profile } = useAuth();
  const { isModuleEnabled } = useOrganizationConfig();
  const firstName = (profile?.full_name || "Teacher").split(" ")[0];

  const [classroom, setClassroom] = useState(null);
  const [totalChildren, setTotalChildren] = useState(0);
  const [loggedToday, setLoggedToday] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      try {
        const classrooms = await listTeacherClassrooms(profile.id);
        const first = classrooms[0];
        if (!first) return;
        setClassroom(first);
        const kids = await listChildrenInClassroom(first.id);
        setTotalChildren(kids.length);
        const today = todayStr();
        const countsPerChild = await Promise.all(
          kids.map(async (c) => {
            const rows = await listReportsForChild(c.id, 1);
            return rows.some((r) => r.report_date === today);
          }),
        );
        setLoggedToday(countsPerChild.filter(Boolean).length);
      } catch {
        // Silently ignore on dashboard — roster page surfaces errors.
      } finally {
        setLoaded(true);
      }
    })();
  }, [profile?.id]);

  return (
    <div data-testid="teacher-dashboard" className="space-y-6">
      <section>
        <p className="overline text-primary-hover">Today</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Good morning, {firstName}
        </h1>
        <p className="mt-2 text-neutral-600">Your classroom at a glance.</p>
      </section>

      {/* First-run reassurance: surface a calm, role-appropriate nudge
         once we know the fetch finished. */}
      {loaded && !classroom && (
        <article
          data-testid="teacher-empty-no-classroom"
          className="rounded-2xl border border-dashed border-neutral-300 bg-white p-5"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
            Getting started
          </p>
          <p className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900">
            You're not assigned to a classroom yet
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            Ask your admin to add you from the Staff page. Once you're assigned,
            your roster, attendance, and photo tools all unlock automatically.
          </p>
        </article>
      )}

      {loaded && classroom && totalChildren > 0 && loggedToday === 0 && (
        <article
          data-testid="teacher-empty-quiet-day"
          className="rounded-2xl border border-primary/20 bg-primary/5 p-5"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-hover">
            What's next
          </p>
          <p className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900">
            {totalChildren} {totalChildren === 1 ? "child" : "children"} on today's roster
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            Open the roster to log meals, naps, and notes — or pop over to Photos
            to share something with families. No rush, you've got this.
          </p>
        </article>
      )}

      <section className="grid grid-cols-2 gap-3">
        <StatCard
          icon={Users}
          label="Logged today"
          value={`${loggedToday} / ${totalChildren || "—"}`}
        />
        <StatCard
          icon={ChalkboardTeacher}
          label="Classroom"
          value={classroom?.name || "—"}
        />
      </section>

      <PWAInstallCard />

      {isModuleEnabled("ratio_capacity") ? (
        <Link
          to="/teacher/ratio-capacity"
          data-testid="teacher-open-ratio-capacity"
          className="flex items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white">
            <TrendUp size={22} weight="duotone" className="text-primary-hover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
              Classroom coverage
            </p>
            <p className="text-sm text-neutral-600">
              Check today’s recorded attendance and coverage for your classroom.
            </p>
          </div>
          <CaretRight size={18} weight="bold" className="text-primary-hover" />
        </Link>
      ) : null}

      {isModuleEnabled("food_program") ? (
        <Link
          to="/teacher/food-program"
          data-testid="teacher-open-food-program"
          className="flex items-center gap-4 rounded-2xl border border-[#C6851E]/20 bg-[#C6851E]/5 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white">
            <ForkKnife size={22} weight="duotone" className="text-[#A66A11]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
              Food service
            </p>
            <p className="text-sm text-neutral-600">
              Count today’s checked-in classroom meal in a few taps.
            </p>
          </div>
          <CaretRight size={18} weight="bold" className="text-[#A66A11]" />
        </Link>
      ) : null}

      <Link
        to="/teacher/roster"
        data-testid="teacher-open-roster"
        className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
          <ClipboardText size={22} weight="duotone" className="text-primary-hover" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            Open today's roster
          </p>
          <p className="text-sm text-neutral-500">
            Tap a child to log meals, naps, diapers, and more.
          </p>
        </div>
        <CaretRight size={18} weight="bold" className="text-neutral-400" />
      </Link>

      <Link
        to="/teacher/photos"
        data-testid="teacher-open-photos"
        className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
          <Camera size={22} weight="duotone" className="text-primary-hover" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            Classroom photos
          </p>
          <p className="text-sm text-neutral-500">
            Snap a quick photo and parents see it in their feed.
          </p>
        </div>
        <CaretRight size={18} weight="bold" className="text-neutral-400" />
      </Link>

      <Link
        to="/teacher/conferences"
        data-testid="teacher-open-conferences"
        className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
          <CalendarCheck size={22} weight="duotone" className="text-primary-hover" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            Conferences
          </p>
          <p className="text-sm text-neutral-500">
            Publish parent-teacher conference slots and review bookings.
          </p>
        </div>
        <CaretRight size={18} weight="bold" className="text-neutral-400" />
      </Link>

      <Link
        to="/teacher/compliance"
        data-testid="teacher-open-compliance"
        className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
          <ShieldCheck size={22} weight="duotone" className="text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            My certifications
          </p>
          <p className="text-sm text-neutral-500">
            See what's on file and what's coming up for renewal.
          </p>
        </div>
        <CaretRight size={18} weight="bold" className="text-neutral-400" />
      </Link>

      {isModuleEnabled("staffing") ? (
        <Link
          to="/teacher/timesheet"
          data-testid="teacher-open-timesheet"
          className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Clock size={22} weight="duotone" className="text-primary-hover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
              Clock in & out
            </p>
            <p className="text-sm text-neutral-500">
              Track your shift hours and review this week.
            </p>
          </div>
          <CaretRight size={18} weight="bold" className="text-neutral-400" />
        </Link>
      ) : null}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-card">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
        <Icon size={18} weight="duotone" className="text-primary-hover" />
      </div>
      <p className="overline mt-3 text-neutral-500">{label}</p>
      <p className="tnum mt-1 font-heading text-xl font-bold tracking-tight text-neutral-900">
        {value}
      </p>
    </div>
  );
}
